/**
 * Whether a signed-out visitor sees the landing page or goes straight to the
 * sign-in page.
 *
 * The landing page is the front door: it shows at "/" only. Everything else
 * keeps the sign-in page it always had: a page asked for by its address (a
 * garden invitation, /today, the subscription), a visitor who came to start
 * a garden by a share's or a garden page's link (?start=1 or ?ref=CODE, which
 * open on signing up as before), someone who has just deleted their account
 * (the sign-in page says it is done), and a sign-in that came back from Google
 * or Facebook with an error to show.
 *
 * Grow's mark on a public page (PublicBar, which goes to "/" with
 * { home: true } in the router's state) always shows the landing page, and
 * forgets an earlier "Start your garden" in this visit.
 *
 * A founding link (?founding=CODE) does show the landing page, with the
 * founding welcome; its code is kept the same, and the button leads on to
 * sign-up.
 *
 * Who uses it: Layout.
 *
 * @module showsLanding
 */

import { forgetStartGarden, keepArrival, startsGarden } from '@/shared/lib/arrivalSource';

/** What decides it. */
interface LandingCase {
  /** The page's path, as the router has it. */
  pathname: string;
  /** The router's state for this visit (the account-deleted note rides in it). */
  state: unknown;
  /** The error a sign-in that came back from Google or Facebook left, if any. */
  redirectError: string | null;
}

/**
 * Whether to show the landing page.
 *
 * @param how - The path, router state and any sign-in error.
 * @returns True for the landing page; false for the sign-in page.
 */
export function showsLanding({ pathname, state, redirectError }: LandingCase): boolean {
  if (pathname !== '/' || redirectError) return false;
  if ((state as { accountDeleted?: boolean } | null)?.accountDeleted) return false;
  // Grow's mark on a public page: the front door, whatever was tapped before.
  if ((state as { home?: boolean } | null)?.home) {
    forgetStartGarden();
    return true;
  }
  // A "Start a garden" link followed inside the app arrives here without the page loading.
  keepArrival(window.location.search);
  return !startsGarden();
}
